import { createContext, useContext, type ReactNode } from 'react';
import { Card as HeroCard } from '@heroui/react/card';
import { cn } from '@/utils/cn';

/**
 * The bordered surface used for every content block — HeroUI's Card.
 *
 * Flat by default; elevation is reserved for modals and hover-lift cards.
 *
 * A `flush` card is one wrapping a full-bleed Table. It renders as HeroUI's
 * `transparent` variant — no border, background or shadow — because the
 * Table already draws its own surround and white body, and a frame around
 * that boxed it in twice. Its Header and Footer drop their rules too: a
 * heading sits above its table separated by whitespace, not by a line.
 */
export interface CardProps {
  children: ReactNode;
  className?: string;
  /** Adds a hover lift. Only for cards that navigate somewhere. */
  interactive?: boolean;
  /** For cards wrapping a full-bleed Table — see above. */
  flush?: boolean;
  /** Anchor for a guided tour step (see `components/tour`). */
  'data-tour'?: string;
}

/** Lets Header/Footer know they sit in a flush (frameless) card. */
const FlushContext = createContext(false);

export function Card({
  children,
  className,
  interactive,
  flush,
  'data-tour': tourAnchor,
}: CardProps) {
  return (
    <FlushContext.Provider value={!!flush}>
      <HeroCard
        data-tour={tourAnchor}
        variant={flush ? 'transparent' : 'default'}
        className={cn(
          // HeroUI's .card carries its own padding; ours belongs to the
          // Header/Body/Footer sections, so clear it on the root.
          'gap-0 rounded-lg p-0',
          !flush && 'border border-neutral-200 bg-white',
          // Lift under a mouse, press under a finger: a tappable card
          // gave no sign on a phone that the tap had landed.
          interactive && 'hover-lift card-press cursor-pointer',
          className,
        )}
      >
        {children}
      </HeroCard>
    </FlushContext.Provider>
  );
}

Card.Header = function CardHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  const flush = useContext(FlushContext);
  return (
    <HeroCard.Header
      className={cn(
        // HeroUI's .card__header hard-codes `flex-direction: column`. It is a
        // plain class rather than a utility, so `flex-row` must be stated
        // explicitly here or every header with `actions` stacks its action
        // under the title instead of aligning it to the far end.
        'flex flex-row items-start justify-between gap-4 p-4 md:p-6',
        !flush && 'border-b border-neutral-200',
        className,
      )}
    >
      <div className="min-w-0 space-y-1">
        <HeroCard.Title className="text-h3 text-foreground">
          {title}
        </HeroCard.Title>
        {description && (
          <HeroCard.Description className="text-small text-muted">
            {description}
          </HeroCard.Description>
        )}
      </div>
      {actions && (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      )}
    </HeroCard.Header>
  );
};

Card.Body = function CardBody({
  children,
  className,
  flush,
}: {
  children: ReactNode;
  className?: string;
  flush?: boolean;
}) {
  return (
    <HeroCard.Content className={cn(!flush && 'p-4 md:p-6', className)}>
      {children}
    </HeroCard.Content>
  );
};

Card.Footer = function CardFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const flush = useContext(FlushContext);
  return (
    <HeroCard.Footer
      className={cn(
        'flex items-center justify-end gap-3 p-4 md:px-6',
        !flush && 'border-t border-neutral-200',
        className,
      )}
    >
      {children}
    </HeroCard.Footer>
  );
};
